import config from 'config';

import { defineStyle, defineStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/styled-system';

const {
  colors: {
    palette: {
      theme: { button: styles }
    }
  }
} = config;

type SchemeStyles = {
  [key: string]: {
    [key: string]: object;
  };
};

const SCHEME_STYLES: SchemeStyles = {
  solid: {
    primary: {
      color: styles.buttonSolidPrimaryColor,
      bg: styles.buttonSolidPrimaryBg
    },
    secondary: {
      color: styles.buttonSolidSecondaryColor,
      bg: styles.buttonSolidSecondaryBg
    },
    secondaryLight: {
      color: styles.buttonSolidSecondaryLightColor,
      bg: styles.buttonSolidSecondaryLightBg
    },
    text: {
      color: styles.buttonSolidTextColor,
      bg: styles.buttonSolidTextBg
    },
    red: {
      color: styles.buttonSolidRedColor,
      bg: styles.buttonSolidRedBg
    },
    black: {
      color: styles.buttonSolidBlackColor,
      bg: styles.buttonSolidBlackBg
    }
  },

  outline: {
    primary: {
      borderColor: styles.buttonOutlinePrimaryBorderColor,
      color: styles.buttonOutlinePrimaryColor
    },
    primaryLight: {
      borderColor: styles.buttonOutlinePrimaryLightBorderColor,
      color: styles.buttonOutlinePrimaryLightColor
    },
    secondary: {
      borderColor: styles.buttonOutlineSecondaryBorderColor,
      color: styles.buttonOutlineSecondaryColor
    },
    secondaryLight: {
      borderColor: styles.buttonOutlineSecondaryLightBorderColor,
      color: styles.buttonOutlineSecondaryLightColor
    },
    black: {
      color: styles.buttonOutlineBlackColor,
      borderColor: styles.buttonOutlineBlackBorder
    }
  },

  brand: {
    primary: {
      bg: styles.buttonBrandPrimaryBg,
      color: styles.buttonBrandPrimaryColor,
      '& svg': {
        fill: styles.buttonBrandPrimarySvgFill
      }
    },
    primaryLight: {
      bg: styles.buttonBrandPrimaryLightBg,
      color: styles.buttonBrandPrimaryLightColor,
      '& svg': {
        fill: styles.buttonBrandPrimaryLightSvgFill
      }
    },
    secondary: {
      bg: styles.buttonBrandSecondaryBg,
      color: styles.buttonBrandSecondaryColor,
      '& svg': {
        fill: styles.buttonBrandSecondarySvgFill
      }
    }
  }
};

const SCHEME_HOVER_STYLES: SchemeStyles = {
  solid: {
    primary: {
      opacity: 0.8
    },
    secondary: {
      opacity: 0.8
    },
    secondaryLight: {
      opacity: 0.8
    },
    text: {
      textDecoration: 'uppercase'
    },
    red: {
      opacity: 0.8
    },
    black: {
      opacity: 0.8
    }
  },

  outline: {
    primary: {
      color: styles.buttonOutlineHoverPrimaryColor,
      bg: styles.buttonOutlineHoverPrimaryBg
    },
    primaryLight: {
      bg: styles.buttonOutlineHoverPrimaryLightBg,
      color: styles.buttonOutlineHoverPrimaryLightColor
    },
    secondary: {
      color: styles.buttonOutlineHoverSecondaryColor,
      bg: styles.buttonOutlineHoverSecondaryBg
    },
    secondaryLight: {
      color: styles.buttonOutlineHoverSecondaryLightColor,
      bg: styles.buttonOutlineHoverSecondaryLightBg
    },
    black: {
      color: styles.buttonOutlineHoverBlackColor,
      bg: styles.buttonOutlineHoverBlackBg
    }
  },

  brand: {
    primary: {
      bg: styles.buttonBrandHoverPrimaryBg,
      color: styles.buttonBrandHoverPrimaryColor,
      '& svg': {
        fill: styles.buttonBrandHoverPrimarySvgFill
      }
    },
    primaryLight: {
      bg: styles.buttonBrandHoverPrimaryLightBg,
      color: styles.buttonBrandHoverPrimaryLightColor,
      '& svg': {
        fill: styles.buttonBrandHoverPrimaryLightSvgFill
      }
    },
    secondary: {
      bg: styles.buttonBrandHoverSecondaryBg,
      color: styles.buttonBrandHoverSecondaryColor,
      '& svg': {
        fill: styles.buttonBrandHoverSecondarySvgFill
      }
    }
  }
};

const outlineStyle = defineStyle(({ colorScheme }: StyleFunctionProps) => ({
  ...SCHEME_STYLES.outline[colorScheme],
  '& svg': {
    transitionProperty: 'all',
    transitionDuration: '.2s'
  },
  textTransform: 'uppercase',
  fontWeight: 'bold',
  _focus: {},
  _focusVisible: {},
  _hover: {
    svg: {
      fill: styles.buttonOutlineHoverPrimaryColor
    },
    ...SCHEME_HOVER_STYLES.outline[colorScheme],
    _disabled: {
      opacity: '0.4',
      ...SCHEME_STYLES.outline[colorScheme]
    }
  },
  _active: {
    ...SCHEME_HOVER_STYLES.outline[colorScheme]
  }
}));

const solidStyle = defineStyle(({ colorScheme }: StyleFunctionProps) => ({
  ...SCHEME_STYLES.solid[colorScheme],
  '& svg': {
    transitionProperty: 'all',
    transitionDuration: '.2s'
  },
  fontWeight: 'bold',
  boxShadow: 'none',
  textTransform: 'uppercase',
  _focus: {
    boxShadow: 'none'
  },
  _focusVisible: {
    boxShadow: 'none'
  },
  _hover: {
    ...SCHEME_HOVER_STYLES.solid[colorScheme],
    _disabled: {
      opacity: '0.4',
      ...SCHEME_STYLES.solid[colorScheme]
    }
  },
  _active: {
    ...SCHEME_HOVER_STYLES.solid[colorScheme]
  }
}));

const brandStyle = defineStyle(({ colorScheme }: StyleFunctionProps) => ({
  ...SCHEME_STYLES.brand[colorScheme],
  fontWeight: 'regular',
  boxShadow: 'none',
  textTransform: 'uppercase',
  _focus: {
    boxShadow: 'none'
  },
  _focusVisible: {
    boxShadow: 'none'
  },
  _hover: {
    ...SCHEME_HOVER_STYLES.brand[colorScheme],
    _disabled: {
      opacity: '0.4',
      ...SCHEME_STYLES.brand[colorScheme]
    }
  },
  _active: {
    ...SCHEME_HOVER_STYLES.brand[colorScheme]
  }
}));

const textStyle = defineStyle({
  color: styles.buttonTextColor,
  minHeight: 8,
  textTransform: 'uppercase'
});

const smStyle = defineStyle({
  fontSize: 12,
  paddingY: 3
});

const mdStyle = defineStyle({
  fontSize: 14,
  letterSpacing: 0.56,
  paddingY: 3
});

const lgStyle = defineStyle({
  fontSize: 16,
  letterSpacing: 0.65,
  lineHeight: 0,
  paddingY: 3
});

const Button = defineStyleConfig({
  baseStyle: {
    fontWeight: 400,
    borderRadius: 25,
    boxShadow: 'none',
    lineHeight: 1,
    _disabled: {
      opacity: 0.4
    },
    _focusVisible: {
      boxShadow: `0 0 0 3px ${styles.focusVisible}`
    }
  },
  sizes: {
    sm: smStyle,
    md: mdStyle,
    lg: lgStyle
  },
  variants: {
    solid: solidStyle,
    outline: outlineStyle,
    brand: brandStyle,
    text: textStyle
  },
  defaultProps: {
    size: 'md',
    variant: 'solid',
    colorScheme: 'primary'
  }
});

export default Button;
